import type {
  DryRunResponse,
  EndpointDto,
  MappingDoc,
  MappingRule,
  MappingSource,
  MappingTarget,
  MappingTransform,
} from '@agent-platform/shared';
import { mappingDocSchema, SDK_OPTIONS } from '@agent-platform/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Background, Controls, MarkerType, ReactFlow, type Edge, type Node } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useFlowColorMode } from '@/hooks/use-flow-color-mode';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ErrorAlert } from '@/components/error-alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { api, ApiError } from '@/lib/api';

const SOURCE_LABELS: Record<MappingSource['from'], string> = {
  header: 'Header',
  query: 'Query parameter',
  body: 'Body (JSONPath)',
  pathParam: 'Path parameter',
  'apiKey.metadata': 'API key metadata',
  env: 'Environment variable',
  const: 'Constant',
};
const TARGET_LABELS: Record<MappingTarget['to'], string> = {
  prompt: 'Prompt variable',
  input: 'User input',
  sdk: 'Model option',
  toolVar: 'Tool variable',
  mcp: 'MCP server field',
  env: 'Run environment',
};
const TRANSFORM_LABELS: Record<MappingTransform['op'], string> = {
  trim: 'Trim',
  lowercase: 'Lowercase',
  uppercase: 'Uppercase',
  stripPrefix: 'Strip prefix',
  regexExtract: 'Regex extract',
  jsonParse: 'Parse JSON',
  template: 'Template',
  default: 'Default value',
  required: 'Required',
  cast: 'Convert type',
};

const defaultSource = (from: MappingSource['from']): MappingSource => {
  switch (from) {
    case 'header':
      return { from, key: 'x-customer-id' };
    case 'query':
      return { from, key: 'lang' };
    case 'body':
      return { from, path: '$.customer.id' };
    case 'pathParam':
      return { from, key: 'publicId' };
    case 'apiKey.metadata':
      return { from, path: '$.plan' };
    case 'env':
      return { from, key: 'MY_VAR' };
    case 'const':
      return { from, value: '' };
  }
};
const defaultTarget = (to: MappingTarget['to']): MappingTarget => {
  switch (to) {
    case 'prompt':
      return { to, var: 'customer' };
    case 'input':
      return { to };
    case 'sdk':
      return { to, option: 'threadId' };
    case 'toolVar':
      return { to, tool: 'tool_name', param: 'param' };
    case 'mcp':
      return { to, server: 'server', field: 'headers.Authorization' };
    case 'env':
      return { to, key: 'MY_VAR' };
  }
};
const defaultTransform = (op: MappingTransform['op']): MappingTransform => {
  switch (op) {
    case 'stripPrefix':
      return { op, value: 'Bearer ' };
    case 'regexExtract':
      return { op, pattern: '(\\d+)', group: 1 };
    case 'template':
      return { op, value: 'Customer {{value}}' };
    case 'default':
      return { op, value: '' };
    case 'cast':
      return { op, type: 'number' };
    default:
      return { op } as MappingTransform;
  }
};

function describeSource(s: MappingSource): string {
  if (s.from === 'const') return `const ${JSON.stringify(s.value)}`;
  if (s.from === 'body' || s.from === 'apiKey.metadata')
    return `${s.from === 'body' ? 'body' : 'key metadata'} ${s.path}`;
  return `${SOURCE_LABELS[s.from].toLowerCase()} ${s.key}`;
}
function describeTarget(t: MappingTarget): string {
  switch (t.to) {
    case 'prompt':
      return `prompt {{${t.var}}}`;
    case 'input':
      return 'user input';
    case 'sdk':
      return `model option ${t.option}`;
    case 'toolVar':
      return `tool ${t.tool}.${t.param}`;
    case 'mcp':
      return `MCP ${t.server} ${t.field}`;
    case 'env':
      return `env ${t.key}`;
  }
}

function Field({
  id,
  label,
  value,
  onChange,
  disabled,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  placeholder?: string;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function SourceFields({
  source,
  onChange,
  disabled,
}: {
  source: MappingSource;
  onChange: (s: MappingSource) => void;
  disabled: boolean;
}) {
  if (source.from === 'const') {
    return (
      <Field
        id="rule-source-value"
        label="Value"
        value={typeof source.value === 'string' ? source.value : JSON.stringify(source.value)}
        disabled={disabled}
        onChange={(v) => onChange({ ...source, value: v })}
      />
    );
  }
  if (source.from === 'body' || source.from === 'apiKey.metadata') {
    return (
      <Field
        id="rule-source-path"
        label="Path"
        value={source.path}
        disabled={disabled}
        placeholder="$.customer.id"
        onChange={(v) => onChange({ ...source, path: v })}
      />
    );
  }
  return (
    <Field
      id="rule-source-key"
      label={source.from === 'header' ? 'Header name' : 'Name'}
      value={source.key}
      disabled={disabled}
      onChange={(v) => onChange({ ...source, key: v })}
    />
  );
}

function TargetFields({
  target,
  onChange,
  disabled,
}: {
  target: MappingTarget;
  onChange: (t: MappingTarget) => void;
  disabled: boolean;
}) {
  switch (target.to) {
    case 'prompt':
      return (
        <Field
          id="rule-target-var"
          label="Variable (use {{name}} in the prompt)"
          value={target.var}
          disabled={disabled}
          onChange={(v) => onChange({ ...target, var: v })}
        />
      );
    case 'input':
      return <p className="text-muted-foreground text-xs">Replaces the caller’s message.</p>;
    case 'sdk':
      return (
        <div className="grid gap-1.5">
          <Label htmlFor="rule-target-option">Option</Label>
          <Select
            value={target.option}
            disabled={disabled}
            onValueChange={(v) => onChange({ ...target, option: v as typeof target.option })}
          >
            <SelectTrigger id="rule-target-option">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SDK_OPTIONS.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    case 'toolVar':
      return (
        <div className="grid grid-cols-2 gap-2">
          <Field
            id="rule-target-tool"
            label="Tool"
            value={target.tool}
            disabled={disabled}
            onChange={(v) => onChange({ ...target, tool: v })}
          />
          <Field
            id="rule-target-param"
            label="Parameter"
            value={target.param}
            disabled={disabled}
            onChange={(v) => onChange({ ...target, param: v })}
          />
        </div>
      );
    case 'mcp':
      return (
        <div className="grid grid-cols-2 gap-2">
          <Field
            id="rule-target-server"
            label="Server"
            value={target.server}
            disabled={disabled}
            onChange={(v) => onChange({ ...target, server: v })}
          />
          <Field
            id="rule-target-field"
            label="Field"
            value={target.field}
            disabled={disabled}
            placeholder="headers.Authorization"
            onChange={(v) => onChange({ ...target, field: v })}
          />
        </div>
      );
    case 'env':
      return (
        <Field
          id="rule-target-key"
          label="Variable"
          value={target.key}
          disabled={disabled}
          onChange={(v) => onChange({ ...target, key: v })}
        />
      );
  }
}

function TransformRow({
  t,
  index,
  onChange,
  onRemove,
  disabled,
}: {
  t: MappingTransform;
  index: number;
  onChange: (t: MappingTransform) => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border p-2">
      <span className="text-muted-foreground w-5 pb-2 text-xs">{index + 1}.</span>
      <div className="grid gap-1.5">
        <Label className="sr-only" htmlFor={`transform-${index}`}>
          Transform {index + 1}
        </Label>
        <Select
          value={t.op}
          disabled={disabled}
          onValueChange={(op) => onChange(defaultTransform(op as MappingTransform['op']))}
        >
          <SelectTrigger id={`transform-${index}`} className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(TRANSFORM_LABELS) as MappingTransform['op'][]).map((op) => (
              <SelectItem key={op} value={op}>
                {TRANSFORM_LABELS[op]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {(t.op === 'stripPrefix' || t.op === 'template' || t.op === 'default') && (
        <Input
          aria-label="Transform value"
          className="w-56"
          disabled={disabled}
          value={typeof t.value === 'string' ? t.value : JSON.stringify(t.value)}
          onChange={(e) => onChange({ ...t, value: e.target.value })}
        />
      )}
      {t.op === 'regexExtract' && (
        <>
          <Input
            aria-label="Pattern"
            className="w-44 font-mono"
            disabled={disabled}
            value={t.pattern}
            onChange={(e) => onChange({ ...t, pattern: e.target.value })}
          />
          <Input
            aria-label="Group"
            type="number"
            className="w-16"
            disabled={disabled}
            value={t.group}
            onChange={(e) => onChange({ ...t, group: Number(e.target.value) })}
          />
        </>
      )}
      {t.op === 'cast' && (
        <Select
          value={t.type}
          disabled={disabled}
          onValueChange={(type) =>
            onChange({ ...t, type: type as 'string' | 'number' | 'boolean' })
          }
        >
          <SelectTrigger aria-label="Type" className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {['string', 'number', 'boolean'].map((ty) => (
              <SelectItem key={ty} value={ty}>
                {ty}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {!disabled && (
        <Button type="button" variant="destructive" size="sm" onClick={onRemove}>
          Remove
        </Button>
      )}
    </div>
  );
}

function RuleForm({
  rule,
  onChange,
  onDelete,
  disabled,
}: {
  rule: MappingRule;
  onChange: (r: MappingRule) => void;
  onDelete: () => void;
  disabled: boolean;
}) {
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-2">
        <Field
          id="rule-id"
          label="Rule id"
          value={rule.id}
          disabled={disabled}
          onChange={(id) => onChange({ ...rule, id })}
        />
        <Field
          id="rule-label"
          label="Label"
          value={rule.label ?? ''}
          disabled={disabled}
          onChange={(label) => onChange({ ...rule, label: label || undefined })}
        />
      </div>
      <fieldset className="grid gap-2">
        <legend className="mb-1 text-sm font-medium">Source</legend>
        <Select
          value={rule.source.from}
          disabled={disabled}
          onValueChange={(from) =>
            onChange({ ...rule, source: defaultSource(from as MappingSource['from']) })
          }
        >
          <SelectTrigger aria-label="Source">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(SOURCE_LABELS) as MappingSource['from'][]).map((f) => (
              <SelectItem key={f} value={f}>
                {SOURCE_LABELS[f]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <SourceFields
          source={rule.source}
          disabled={disabled}
          onChange={(source) => onChange({ ...rule, source })}
        />
      </fieldset>
      <fieldset className="grid gap-2">
        <legend className="mb-1 text-sm font-medium">Transforms (applied in order)</legend>
        {rule.transforms.map((t, i) => (
          <TransformRow
            key={i}
            t={t}
            index={i}
            disabled={disabled}
            onChange={(next) =>
              onChange({ ...rule, transforms: rule.transforms.map((x, j) => (j === i ? next : x)) })
            }
            onRemove={() =>
              onChange({ ...rule, transforms: rule.transforms.filter((_, j) => j !== i) })
            }
          />
        ))}
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit"
            onClick={() => onChange({ ...rule, transforms: [...rule.transforms, { op: 'trim' }] })}
          >
            Add transform
          </Button>
        )}
      </fieldset>
      <fieldset className="grid gap-2">
        <legend className="mb-1 text-sm font-medium">Target</legend>
        <Select
          value={rule.target.to}
          disabled={disabled}
          onValueChange={(to) =>
            onChange({ ...rule, target: defaultTarget(to as MappingTarget['to']) })
          }
        >
          <SelectTrigger aria-label="Target">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(TARGET_LABELS) as MappingTarget['to'][]).map((t) => (
              <SelectItem key={t} value={t}>
                {TARGET_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <TargetFields
          target={rule.target}
          disabled={disabled}
          onChange={(target) => onChange({ ...rule, target })}
        />
      </fieldset>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={rule.secret}
          disabled={disabled}
          onChange={(e) => onChange({ ...rule, secret: e.target.checked })}
        />
        Secret — never logged, stored or shown (not allowed for prompt variables)
      </label>
      {!disabled && (
        <Button type="button" variant="destructive" className="w-fit" onClick={onDelete}>
          Delete rule
        </Button>
      )}
    </div>
  );
}

function Canvas({
  doc,
  selected,
  onSelect,
}: {
  doc: MappingDoc;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const colorMode = useFlowColorMode();
  const { nodes, edges } = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    doc.rules.forEach((rule, i) => {
      const y = i * 100;
      const style =
        rule.id === selected
          ? { borderColor: 'var(--ring)', boxShadow: '0 0 0 1px var(--ring)' }
          : undefined;
      const transforms = rule.transforms.map((t) => TRANSFORM_LABELS[t.op]).join(' → ') || 'as is';
      nodes.push(
        {
          id: `${i}:s`,
          position: { x: 0, y },
          data: { label: describeSource(rule.source) },
          style,
          sourcePosition: 'right' as never,
          targetPosition: 'left' as never,
        },
        {
          id: `${i}:t`,
          position: { x: 240, y },
          data: { label: transforms },
          style,
          sourcePosition: 'right' as never,
          targetPosition: 'left' as never,
        },
        {
          id: `${i}:g`,
          position: { x: 480, y },
          data: { label: `${describeTarget(rule.target)}${rule.secret ? ' 🔒' : ''}` },
          style,
          sourcePosition: 'right' as never,
          targetPosition: 'left' as never,
        },
      );
      const marker = { type: MarkerType.ArrowClosed };
      edges.push(
        { id: `${i}:a`, source: `${i}:s`, target: `${i}:t`, markerEnd: marker },
        { id: `${i}:b`, source: `${i}:t`, target: `${i}:g`, markerEnd: marker },
      );
    });
    return { nodes, edges };
  }, [doc, selected]);

  return (
    <div className="h-[420px] rounded-lg border" aria-label="Mapping canvas">
      <ReactFlow
        colorMode={colorMode}
        defaultMarkerColor="var(--muted-foreground)"
        nodes={nodes}
        edges={edges}
        fitView
        nodesDraggable={false}
        nodesConnectable={false}
        onNodeClick={(_, node) => onSelect(doc.rules[Number(node.id.split(':')[0])]!.id)}
      >
        <Background />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

function DryRun({ endpoint, doc }: { endpoint: EndpointDto; doc: MappingDoc }) {
  const [headers, setHeaders] = useState('{\n  "x-customer-id": "c-42"\n}');
  const [query, setQuery] = useState('{}');
  const [body, setBody] = useState('{\n  "input": "Hello"\n}');
  const [metadata, setMetadata] = useState('{}');
  const [parseError, setParseError] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: (payload: unknown) =>
      api<DryRunResponse>(`/endpoints/${endpoint.id}/mapping/dry-run`, {
        method: 'POST',
        body: payload,
      }),
  });

  const submit = () => {
    try {
      const payload = {
        headers: JSON.parse(headers),
        query: JSON.parse(query),
        body: JSON.parse(body),
        apiKeyMetadata: JSON.parse(metadata),
        mapping: doc,
      };
      setParseError(null);
      run.mutate(payload);
    } catch {
      setParseError('Each sample field must be valid JSON.');
    }
  };

  const area = (id: string, label: string, value: string, set: (v: string) => void) => (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Textarea
        id={id}
        className="min-h-24 font-mono text-xs"
        value={value}
        onChange={(e) => set(e.target.value)}
      />
    </div>
  );

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="grid gap-3">
        {area('dry-headers', 'Headers', headers, setHeaders)}
        {area('dry-query', 'Query', query, setQuery)}
        {area('dry-body', 'Body', body, setBody)}
        {area('dry-metadata', 'API key metadata', metadata, setMetadata)}
        {parseError && <p className="text-destructive text-sm">{parseError}</p>}
        <Button className="w-fit" onClick={submit} disabled={run.isPending}>
          Run dry run
        </Button>
      </div>
      <div className="grid content-start gap-3">
        <ErrorAlert error={run.error} />
        {run.data && (
          <>
            <p className="text-sm font-medium" data-testid="dry-run-status">
              {run.data.ok
                ? 'Mapping resolved'
                : `Mapping failed: ${run.data.errors.map((e) => `${e.ruleId} (${e.code})`).join(', ')}`}
            </p>
            <div>
              <Label>System prompt</Label>
              <pre
                className="bg-muted mt-1 rounded p-2 text-xs whitespace-pre-wrap"
                data-testid="dry-run-prompt"
              >
                {run.data.systemPrompt || '(empty)'}
              </pre>
            </div>
            <div>
              <Label>Resolved values (secrets masked)</Label>
              <pre
                className="bg-muted mt-1 max-h-80 overflow-auto rounded p-2 text-xs"
                data-testid="dry-run-resolved"
              >
                {JSON.stringify(run.data.resolved, null, 2)}
              </pre>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function MappingEditor({
  endpoint,
  tenantId,
  canEdit,
}: {
  endpoint: EndpointDto;
  tenantId: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [doc, setDoc] = useState<MappingDoc>(endpoint.mapping);
  const [selected, setSelected] = useState<string | null>(endpoint.mapping.rules[0]?.id ?? null);
  const [json, setJson] = useState(() => JSON.stringify(endpoint.mapping, null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const dirty = JSON.stringify(doc) !== JSON.stringify(endpoint.mapping);

  const save = useMutation({
    mutationFn: () =>
      api<EndpointDto>(`/endpoints/${endpoint.id}/mapping`, {
        method: 'PUT',
        body: { mapping: doc },
      }),
    onSuccess: (updated) => {
      toast.success(`Mapping saved (version ${updated.mappingVersion})`);
      queryClient.setQueryData(['endpoint', tenantId, endpoint.id], updated);
    },
  });

  const update = (next: MappingDoc) => {
    setDoc(next);
    setJson(JSON.stringify(next, null, 2));
  };
  const index = doc.rules.findIndex((r) => r.id === selected);
  const rule = index >= 0 ? doc.rules[index] : undefined;
  const addRule = () => {
    let n = doc.rules.length + 1;
    while (doc.rules.some((r) => r.id === `rule_${n}`)) n++;
    const id = `rule_${n}`;
    update({
      ...doc,
      rules: [
        ...doc.rules,
        {
          id,
          source: defaultSource('header'),
          transforms: [],
          target: defaultTarget('prompt'),
          secret: false,
        },
      ],
    });
    setSelected(id);
  };
  const ruleErrors = save.error instanceof ApiError ? save.error.errors : [];

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="text-base">Request mapping</CardTitle>
          <CardDescription>
            Turn headers, query, body and key metadata into prompt variables, the user input and
            model options. Saved as version {endpoint.mappingVersion}.
          </CardDescription>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={addRule}>
              Add rule
            </Button>
            <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
              {save.isPending ? 'Saving…' : dirty ? 'Save mapping' : 'Saved'}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="grid gap-4">
        <ErrorAlert error={save.error} />
        {ruleErrors.length > 0 && (
          <ul className="text-destructive list-disc pl-5 text-sm">
            {ruleErrors.map((e) => (
              <li key={e.path + e.message}>
                {e.path.replace(
                  /^mapping\.rules\.(\d+)/,
                  (_, i: string) => `Rule ${doc.rules[Number(i)]?.id ?? Number(i) + 1}`,
                )}
                : {e.message}
              </li>
            ))}
          </ul>
        )}
        <Tabs defaultValue="canvas">
          <TabsList>
            <TabsTrigger value="canvas">Canvas</TabsTrigger>
            <TabsTrigger value="json">JSON</TabsTrigger>
            <TabsTrigger value="dry-run">Dry run</TabsTrigger>
          </TabsList>
          <TabsContent value="canvas">
            {doc.rules.length === 0 ? (
              <p className="text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm">
                No rules. Calls pass the caller’s input straight to the agent.
                {canEdit && ' Add a rule to map request data.'}
              </p>
            ) : (
              <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_24rem]">
                <Canvas doc={doc} selected={selected} onSelect={setSelected} />
                {rule ? (
                  <RuleForm
                    key={index}
                    rule={rule}
                    disabled={!canEdit}
                    onChange={(next) => {
                      update({ ...doc, rules: doc.rules.map((r, i) => (i === index ? next : r)) });
                      if (next.id !== rule.id) setSelected(next.id);
                    }}
                    onDelete={() => {
                      update({ ...doc, rules: doc.rules.filter((_, i) => i !== index) });
                      setSelected(doc.rules[index === 0 ? 1 : 0]?.id ?? null);
                    }}
                  />
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Select a rule on the canvas to edit it.
                  </p>
                )}
              </div>
            )}
          </TabsContent>
          <TabsContent value="json">
            <div className="grid gap-2">
              <Label htmlFor="mapping-json">Mapping document</Label>
              <Textarea
                id="mapping-json"
                className="min-h-96 font-mono text-xs"
                value={json}
                disabled={!canEdit}
                onChange={(e) => setJson(e.target.value)}
              />
              {jsonError && (
                <p className="text-destructive text-sm whitespace-pre-wrap">{jsonError}</p>
              )}
              {canEdit && (
                <Button
                  variant="outline"
                  className="w-fit"
                  onClick={() => {
                    try {
                      const parsed = mappingDocSchema.safeParse(JSON.parse(json));
                      if (!parsed.success) {
                        setJsonError(
                          parsed.error.issues
                            .map((i) => `${i.path.join('.')}: ${i.message}`)
                            .join('\n'),
                        );
                        return;
                      }
                      setJsonError(null);
                      update(parsed.data);
                      setSelected(parsed.data.rules[0]?.id ?? null);
                      toast.success('Applied — save to keep it');
                    } catch {
                      setJsonError('This is not valid JSON.');
                    }
                  }}
                >
                  Apply JSON
                </Button>
              )}
            </div>
          </TabsContent>
          <TabsContent value="dry-run">
            <DryRun endpoint={endpoint} doc={doc} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
